function fmtNum(n) {
  return Number(parseFloat(n) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function calcUltimaFaixa(faixas, tipoPagamento, porOcorrencia = false) {
  if (!faixas || faixas.length === 0) return 0;
  const ultima = faixas[faixas.length - 1];
  const bonus = parseFloat(ultima.bonus) || 0;
  if (tipoPagamento !== 1) return bonus;
  // Por ocorrência: cada unidade que atende conta 1 (não multiplica pelo máximo da faixa)
  const mult = porOcorrencia ? 1 : (parseFloat(ultima.maximo) || 0);
  return mult * bonus;
}

// 'O' = indicador apurado por ocorrência (conta 1 por unidade que atende ao critério)
function ehOcorrencia(ind) {
  return String((ind && ind.tipo_apuracao) || "").trim().toUpperCase() === "O";
}

/**
 * Calcula porColaborador para tipos Modelo, Peça e Serviço.
 * @param {Array}    itens        - form.modelosSelecionados
 * @param {Object}   metas        - form.metasModelos
 * @param {Function} getKey       - (item) => chave usada em metas (ex: m => m.id_produto)
 * @param {number}   tipoCalculo  - 1 = Meta Fixa, 2 = Faixa Progressiva
 * @param {number}   tipoPagamento - 1 = Por Unidade, 2 = Valor Fixo
 */
export function calcPorColaboradorItens(itens, metas, getKey, tipoCalculo, tipoPagamento) {
  let porColaborador = 0;
  itens.forEach(item => {
    const entry = metas[getKey(item)] || {};
    if (tipoCalculo === 2) {
      porColaborador += calcUltimaFaixa(entry.faixas, tipoPagamento);
    } else {
      const metaValor = parseFloat(entry.metaValor) || 0;
      const valorBonus = parseFloat(entry.valorBonus) || 0;
      porColaborador += tipoPagamento === 1 ? metaValor * valorBonus : valorBonus;
    }
  });
  return porColaborador;
}

/**
 * Calcula porColaborador para tipo Indicador.
 * @param {Array}  indicadores       - form.indicadoresSelecionados
 * @param {Object} metasIndicadores  - form.metasIndicadores
 * @param {number} tipoCalculo       - 1 = Meta Fixa, 2 = Faixa Progressiva
 * @param {number} tipoPagamento     - 1 = Por Unidade, 2 = Valor Fixo
 * @param {*}      valorBonus        - form.valorBonus (usado apenas no tipoCalculo 1)
 */
export function calcPorColaboradorIndicadores(indicadores, metasIndicadores, tipoCalculo, tipoPagamento, valorBonus) {
  const bonus = parseFloat(valorBonus) || 0;
  if (tipoCalculo === 2) {
    return indicadores.reduce((sum, ind) => {
      const entry = metasIndicadores[ind.id_indicador] || {};
      return sum + calcUltimaFaixa(entry.faixas, tipoPagamento, ehOcorrencia(ind));
    }, 0);
  }
  if (tipoPagamento === 1) {
    const totalMeta = indicadores.reduce(
      (sum, ind) =>
        sum +
        (ehOcorrencia(ind)
          ? 1
          : parseFloat(metasIndicadores[ind.id_indicador]?.metaValor) || 0),
      0
    );
    return totalMeta * bonus;
  }
  return bonus;
}

/**
 * Calcula custo estimado para tipo Chassi.
 * @param {Array} chassisSelecionados - form.chassisSelecionados
 */
export function calcCustoEstimadoChassi(chassisSelecionados) {
  const total = chassisSelecionados.reduce(
    (sum, c) => sum + (parseFloat(c.valor_bonus) || 0),
    0
  );
  return { porColaborador: null, total, nSemanas: 1, linhas: gerarLinhasChassi(chassisSelecionados) };
}

/**
 * Gera linhas de detalhamento para Chassi.
 */
export function gerarLinhasChassi(chassisSelecionados) {
  return chassisSelecionados
    .filter(c => parseFloat(c.valor_bonus) > 0)
    .map(c => ({
      label: `${c.chassi_completo} — ${c.descricao_modelo}`,
      detalhe: "bônus único",
      valor: parseFloat(c.valor_bonus) || 0
    }));
}

/**
 * Gera linhas de detalhamento para Modelo, Peça e Serviço.
 * @param {Array}    itens         - form.modelosSelecionados
 * @param {Object}   metas         - form.metasModelos
 * @param {Function} getKey        - (item) => chave usada em metas
 * @param {Function} getNome       - (item) => string descritiva do item
 * @param {number}   tipoCalculo
 * @param {number}   tipoPagamento
 */
export function gerarLinhasItens(itens, metas, getKey, getNome, tipoCalculo, tipoPagamento) {
  return itens.map(item => {
    const entry = metas[getKey(item)] || {};
    const nome = getNome(item);

    if (tipoCalculo === 2) {
      const faixas = entry.faixas || [];
      const ultima = faixas[faixas.length - 1] || {};
      const max = parseFloat(ultima.maximo) || 0;
      const bonus = parseFloat(ultima.bonus) || 0;
      if (!max && !bonus) return { label: nome, detalhe: "sem faixa configurada", valor: 0 };
      if (tipoPagamento === 1) {
        return { label: nome, detalhe: `últ. faixa: ${max} un × R$ ${fmtNum(bonus)}/un`, valor: max * bonus };
      }
      return { label: nome, detalhe: `últ. faixa: bônus fixo`, valor: bonus };
    }

    const metaValor = parseFloat(entry.metaValor) || 0;
    const valorBonus = parseFloat(entry.valorBonus) || 0;
    if (tipoPagamento === 1) {
      return { label: nome, detalhe: `${metaValor} un × R$ ${fmtNum(valorBonus)}/un`, valor: metaValor * valorBonus };
    }
    return { label: nome, detalhe: "bônus fixo", valor: valorBonus };
  });
}

/**
 * Gera linhas de detalhamento para Indicador.
 */
export function gerarLinhasIndicadores(indicadores, metasIndicadores, tipoCalculo, tipoPagamento, valorBonus) {
  const bonus = parseFloat(valorBonus) || 0;

  if (tipoCalculo === 2) {
    return indicadores.map(ind => {
      const entry = metasIndicadores[ind.id_indicador] || {};
      const faixas = entry.faixas || [];
      const ultima = faixas[faixas.length - 1] || {};
      const max = parseFloat(ultima.maximo) || 0;
      const b = parseFloat(ultima.bonus) || 0;
      const isOc = ehOcorrencia(ind);
      if (tipoPagamento === 1) {
        return {
          label: `${ind.id_indicador} — ${ind.indicador}`,
          detalhe: !b
            ? "sem faixa configurada"
            : isOc
              ? `por ocorrência: 1 × R$ ${fmtNum(b)}`
              : `últ. faixa: ${max} un × R$ ${fmtNum(b)}/un`,
          valor: (isOc ? 1 : max) * b
        };
      }
      return {
        label: `${ind.id_indicador} — ${ind.indicador}`,
        detalhe: b ? `últ. faixa: bônus fixo` : "sem faixa configurada",
        valor: b
      };
    });
  }

  if (tipoPagamento === 1) {
    return indicadores.map(ind => {
      const isOc = ehOcorrencia(ind);
      const meta = parseFloat(metasIndicadores[ind.id_indicador]?.metaValor) || 0;
      if (isOc) {
        return {
          label: `${ind.id_indicador} — ${ind.indicador}`,
          detalhe: bonus ? `por ocorrência: 1 × R$ ${fmtNum(bonus)}` : "bônus não preenchido",
          valor: bonus
        };
      }
      return {
        label: `${ind.id_indicador} — ${ind.indicador}`,
        detalhe: meta && bonus ? `${meta} un × R$ ${fmtNum(bonus)}/un` : "meta não preenchida",
        valor: meta * bonus
      };
    });
  }

  // Valor Fixo — um único bônus ao atingir todos os indicadores
  return [{
    label: "Bônus fixo ao atingir todas as metas",
    detalhe: "",
    valor: bonus
  }];
}

/**
 * Bonus do participante de cada colaborador, por origem do bonus.
 *
 * Origem 1 (Informado): o mesmo valor fixo para todo mundo que bate a meta.
 *
 * Origem 2 (% sobre referencia): percentual sobre a comissao P2 daquele
 * colaborador na competencia da campanha, que TabelaMetasCalculadas busca uma
 * vez e deixa em form.comissaoParticipantes. Colaborador sem comissao na
 * competencia entra como zero, nao como ausente: a comissao do mes corrente
 * ainda cresce e a apuracao vai atualizar o bonus conforme ela sobe — hoje
 * ele vale zero, e zero e um numero, nao uma incognita. Mesma decisao de
 * calcularBonusReferencia em bonusReferenciaUtils.js.
 *
 * @param {Object} form - novaMetaForm do wizard
 * @returns {Object.<string, number>} mapa id_colaborador (string) -> bonus
 */
export function bonusParticipantePorColaborador(form) {
  if (!form.usaMetaColaborador) return {};
  const colaboradores = form.colaboradoresSelecionados || [];
  const porReferencia = Number(form.origemBonusColaborador) === 2;
  const comissoes = form.comissaoParticipantes || {};
  const percentual = parseFloat(form.comissaoPercentual) || 0;
  const fixo = parseFloat(form.valorBonusColaborador) || 0;

  const mapa = {};
  colaboradores.forEach(c => {
    const chave = String(c.id_colaborador);
    mapa[chave] = porReferencia
      ? (percentual / 100) * (parseFloat(comissoes[chave]) || 0)
      : fixo;
  });
  return mapa;
}

/**
 * Contribuicao do bonus do participante (camada opcional, exclusiva do
 * objetivo Modelo) para o custo estimado — usada tanto pelo resumo lateral
 * (ModalCadastroMeta) quanto pelo card da etapa (SubEtapaCalculoModelo), pra
 * eles nunca divergirem de novo.
 *
 * Na origem "Informado" o valor e igual para todos, entao porColaborador e o
 * proprio valor. Na origem "% sobre referencia" cada um tem o seu, e
 * porColaborador vira a media — o que mantem a identidade
 * `porColaborador * n === total` de que os dois consumidores dependem. Quem
 * precisa do valor individual (o modo "meta individual" do card) usa
 * bonusParticipantePorColaborador direto.
 *
 * `sobreReferencia` sinaliza que o numero e estimativa sobre a comissao de um
 * mes que ainda nao fechou — nao e "nao estimavel" (era, ate a comissao
 * passar a ser buscada); quem exibe usa isso pra qualificar o texto.
 *
 * @param {Object} form - novaMetaForm do wizard
 * @returns {{ porColaborador: number, total: number, sobreReferencia: boolean }}
 */
export function calcCustoParticipante(form) {
  if (!form.usaMetaColaborador) {
    return { porColaborador: 0, total: 0, sobreReferencia: false };
  }
  const mapa = bonusParticipantePorColaborador(form);
  const valores = Object.values(mapa);
  const total = valores.reduce((s, v) => s + v, 0);

  if (Number(form.origemBonusColaborador) === 2) {
    return {
      porColaborador: valores.length ? total / valores.length : 0,
      total,
      sobreReferencia: true
    };
  }
  return {
    porColaborador: parseFloat(form.valorBonusColaborador) || 0,
    total,
    sobreReferencia: false
  };
}
